import { FilterColors, FilterFonts } from "../../state/filter";
import { HostId, IsPreviewGenerator } from "../../state/ui";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import { ReactElement, memo, useContext, useRef } from "react";
import { SDK_Title, strings } from "@quickpoint.me/shared";
import { elementAlign, opacityHelper } from "../../lib/cssHelpers";
import styled, { DefaultTheme, ThemeProvider } from "styled-components";

import { ElementProps } from "../../functionalComponents/RenderPage";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import MarkDownToTag from "../../lib/markdown/MarkDown";
import { OwnerName } from "../../state/selectors";
import { PositionContext } from "../displayElements/DisplayElements";
import { TitleProps } from "../../@types/viewerati";
import createTestId from "../../lib/createTestId";
import { mainCssRules } from "../../lib/cssTemplates";
import modifierStyled from "../../lib/modifierStyled";
import { NeuroDivergentFontMultiplier } from "../../state/accessibility";
import useAiAnimation from "../../lib/animations/aiAnimationHook";
import { useRecoilValue } from "recoil";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";

const StyledTitle = styled.h2<TitleProps & { $fontMultiplier: number; $baseFontSizeRaw: number }>`
  margin: 0;
  text-wrap: auto;
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  ${(props) => elementAlign(props.theme.align, props.$isInFlexBox)};
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  position: relative;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`}
  ${(props) => `${modifierStyled("title:", props)}`};

`;

const Title = ({
	atomId,
	pageIndex,
	type,
}: ElementProps): ReactElement<ElementProps> => {
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const elRef = useRef(null);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const pageSize = useRecoilValue(PageSizeAtom);
	const baseFontSize = pageSize.baseFontSize;
	const tapped = elementData.tapped;
	const fonts = useRecoilValue(FilterFonts);
	const ownerName = useRecoilValue(OwnerName);
	const position = useContext(PositionContext);
	const hostId = useRecoilValue(HostId);

	useTappedAnimation({ elRef, tapped, atomId });
	useAiAnimation({ elRef, modifier: elementData.modifier });

	const content =
		typeof elementData.content === "string"
			? MarkDownToTag({
					text: elementData.content,
					ownerName,
					modifier: elementData.modifier,
					hostId,
					tapped,
				})
			: elementData.content;

	const titleFilter = (theme: DefaultTheme): SDK_Title => {
		return theme.title;
	};
	if (!elementData.content) return <></>;
	return (
		<ThemeProvider theme={titleFilter}>
			<StyledTitle
				as={pageIndex === 1 ? "h1" : "h2"}
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fonts={fonts}
				$fontMultiplier={fontMultiplier}
				$isInFlexBox={isInFlexBox}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				className={`${strings.css.pageElement}  title`}
				data-atom-id={elementData.id}
				data-testid={createTestId({ atomId, position, type, isPreviewer })}
				ref={elRef}
			>
				{content}
			</StyledTitle>
		</ThemeProvider>
	);
};

export default memo(Title);
